import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { API, useAuth } from "../App";
import { Section, GlassCard, Eyebrow } from "../components/Section";
import {
  Inbox,
  Briefcase,
  MapPin,
  AlertCircle,
  Check,
  X,
  Loader2,
  Clock,
  ChevronRight,
  Sparkles,
} from "lucide-react";

const urgencyTone = {
  high: "bg-red-400/10 text-red-300 border border-red-400/30",
  medium: "bg-gold-400/10 text-gold-200 border border-gold-400/30",
  low: "bg-turquoise/10 text-turquoise border border-turquoise/30",
};

const statusFlow = [
  { v: "accepted", l: "Accepted" },
  { v: "consultation_scheduled", l: "Consultation Scheduled" },
  { v: "engagement_active", l: "Engagement Active" },
  { v: "filing_stage", l: "Filing Stage" },
  { v: "hearing_stage", l: "Hearing Stage" },
  { v: "judgment_pending", l: "Judgment Pending" },
  { v: "matter_closed", l: "Matter Closed" },
];

const LawyerInbox = () => {
  const { user, loading } = useAuth();
  const [openRequests, setOpenRequests] = useState([]);
  const [myCases, setMyCases] = useState([]);
  const [tab, setTab] = useState("inbox");
  const [busy, setBusy] = useState(true);
  const [actionId, setActionId] = useState(null);

  const fetchInbox = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/lawyer-requests/inbox`, { withCredentials: true });
      setOpenRequests(data.open_requests || []);
      setMyCases(data.my_cases || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load inbox");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (user && (user.role === "lawyer" || user.role === "admin")) {
      fetchInbox();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" state={{ from: { pathname: "/lawyer/inbox" } }} replace />;
  if (user.role !== "lawyer" && user.role !== "admin") {
    return (
      <main className="min-h-[calc(100vh-5rem)] flex items-center justify-center bg-ink text-white px-6">
        <div className="text-center max-w-md">
          <Eyebrow>Lawyers only</Eyebrow>
          <h1 className="font-playfair text-4xl text-white mt-3 mb-4">This is the lawyer inbox.</h1>
          <p className="font-inter text-sm text-white/60 mb-6">
            You&apos;ll need a verified lawyer account to access matched client requests and cases.
          </p>
          <Link to="/for-lawyers" className="btn-aj-primary">Become a Justice-AI Lawyer</Link>
        </div>
      </main>
    );
  }

  const accept = async (id) => {
    setActionId(id);
    try {
      await axios.post(`${API}/lawyer-requests/${id}/accept`, {}, { withCredentials: true });
      toast.success("Case accepted — added to your active matters.");
      fetchInbox();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept");
    } finally {
      setActionId(null);
    }
  };

  const decline = async (id) => {
    setActionId(id);
    try {
      await axios.post(`${API}/lawyer-requests/${id}/decline`, {}, { withCredentials: true });
      toast.success("Declined. Routed to next match.");
      fetchInbox();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to decline");
    } finally {
      setActionId(null);
    }
  };

  const updateStatus = async (id, status) => {
    setActionId(id);
    try {
      await axios.post(`${API}/lawyer-requests/${id}/status`, { status }, { withCredentials: true });
      toast.success(`Status updated to "${status.replace(/_/g, " ")}"`);
      fetchInbox();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally {
      setActionId(null);
    }
  };

  return (
    <main className="bg-ink text-white min-h-[calc(100vh-5rem)] py-10" data-testid="lawyer-inbox-page">
      <div className="container mx-auto px-5 sm:px-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <Eyebrow>Lawyer Operating System</Eyebrow>
            <h1 className="font-playfair text-3xl md:text-5xl text-white mt-3 leading-tight">
              Your <span className="italic text-aj-gradient">matched matters.</span>
            </h1>
            <p className="font-inter text-sm text-white/55 mt-2">
              Pilot Phase — Free until further notice. Accept matters, manage cases through milestones.
            </p>
          </div>
          <div className="flex gap-3">
            <Link to="/lawyer-subscribe" data-testid="inbox-sub-btn" className="btn-aj-secondary !py-2.5">
              <Sparkles className="w-4 h-4" /> Pilot Plan
            </Link>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: "inbox", l: "Inbox", count: openRequests.length, icon: Inbox },
            { id: "cases", l: "My Active Cases", count: myCases.length, icon: Briefcase },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`inbox-tab-${t.id}`}
              className={`flex items-center gap-2 px-5 py-3 rounded-full font-montserrat text-xs uppercase tracking-wider transition-all ${
                tab === t.id
                  ? "bg-turquoise text-ink font-semibold"
                  : "bg-transparent text-white/70 border border-white/15 hover:border-white/40 hover:text-white"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.l}
              <span className="ml-1 px-2 rounded-full bg-black/30 text-[10px]">{t.count}</span>
            </button>
          ))}
        </div>

        {busy && (
          <div className="flex items-center gap-2 text-white/55 font-inter text-sm mb-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {/* Inbox tab */}
        {tab === "inbox" && (
          <div className="space-y-4">
            {openRequests.length === 0 && !busy && (
              <GlassCard>
                <p className="font-inter text-sm text-white/45 text-center py-6">No open requests in your inbox right now.</p>
              </GlassCard>
            )}
            {openRequests.map((r, i) => (
              <motion.div key={r.request_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: i * 0.05 }}>
                <GlassCard glow data-testid={`inbox-request-${i}`}>
                  <div className="grid lg:grid-cols-12 gap-5">
                    <div className="lg:col-span-8">
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className="aj-chip !py-1 !text-[9px]">{r.category}</span>
                        <span className={`px-2.5 py-0.5 rounded-full font-montserrat text-[10px] uppercase tracking-wider ${urgencyTone[r.urgency] || urgencyTone.medium}`}>
                          {r.urgency} urgency
                        </span>
                        {r.is_wallet_eligible && (
                          <span className="aj-chip aj-chip-gold !py-1 !text-[9px]">Justice Wallet</span>
                        )}
                        {r.match_score && (
                          <span className="aj-chip !py-1 !text-[9px]">Match {r.match_score}%</span>
                        )}
                      </div>
                      <h3 className="font-playfair text-2xl text-white mb-1">{r.citizen_name}</h3>
                      <div className="flex items-center gap-3 font-inter text-xs text-white/55 mb-3">
                        <MapPin className="w-3.5 h-3.5" /> {r.location}
                        <Clock className="w-3.5 h-3.5 ml-2" /> {new Date(r.created_at).toLocaleString()}
                      </div>
                      <p className="font-inter text-sm text-white/75 leading-relaxed">{r.summary}</p>
                    </div>
                    <div className="lg:col-span-4 flex flex-col gap-3 lg:items-end">
                      <button
                        onClick={() => accept(r.request_id)}
                        disabled={actionId === r.request_id}
                        data-testid={`inbox-accept-${i}`}
                        className="btn-aj-primary !py-2.5 w-full lg:w-auto disabled:opacity-60"
                      >
                        {actionId === r.request_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        Accept Case
                      </button>
                      <button
                        onClick={() => decline(r.request_id)}
                        disabled={actionId === r.request_id}
                        data-testid={`inbox-decline-${i}`}
                        className="btn-aj-ghost !py-2 w-full lg:w-auto justify-center"
                      >
                        <X className="w-4 h-4" /> Decline
                      </button>
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        )}

        {/* My cases */}
        {tab === "cases" && (
          <div className="space-y-4">
            {myCases.length === 0 && !busy && (
              <GlassCard>
                <p className="font-inter text-sm text-white/45 text-center py-6">
                  You don&apos;t have any active cases yet. Accept a matter from your inbox to get started.
                </p>
              </GlassCard>
            )}
            {myCases.map((c, i) => (
              <motion.div key={c.request_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: i * 0.05 }}>
                <GlassCard data-testid={`case-${i}`}>
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="aj-chip !py-1 !text-[9px]">{c.category}</span>
                    <span className="aj-chip aj-chip-gold !py-1 !text-[9px] capitalize">{c.status.replace(/_/g, " ")}</span>
                  </div>
                  <h3 className="font-playfair text-2xl text-white mb-1">{c.citizen_name}</h3>
                  <div className="flex items-center gap-3 font-inter text-xs text-white/55 mb-3">
                    <MapPin className="w-3.5 h-3.5" /> {c.location}
                    <Clock className="w-3.5 h-3.5 ml-2" /> Accepted {c.accepted_at ? new Date(c.accepted_at).toLocaleString() : "—"}
                  </div>
                  <p className="font-inter text-sm text-white/75 leading-relaxed mb-5">{c.summary}</p>

                  {/* Status pipeline */}
                  <div className="rounded-xl border border-white/8 bg-white/[0.02] p-4">
                    <Eyebrow className="mb-3">Advance the case</Eyebrow>
                    <div className="flex flex-wrap gap-2">
                      {statusFlow.map((s) => (
                        <button
                          key={s.v}
                          onClick={() => updateStatus(c.request_id, s.v)}
                          disabled={actionId === c.request_id}
                          data-testid={`case-status-${i}-${s.v}`}
                          className={`px-3 py-1.5 rounded-full font-montserrat text-[10px] uppercase tracking-wider font-semibold transition-all ${
                            c.status === s.v
                              ? "bg-turquoise text-ink"
                              : "bg-white/[0.03] border border-white/10 text-white/65 hover:border-turquoise/40"
                          } disabled:opacity-60`}
                        >
                          {s.l}
                        </button>
                      ))}
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

export default LawyerInbox;
